/**
 * The GA4 destination adapter.
 *
 * Everything that describes *what an event is* lives in `./events` and is
 * shared with every other destination. This file holds only what is true of
 * GA4 specifically: its collection limits, its 100-event budget and the
 * allow-list that respects it, and Consent Mode v2.
 *
 * The lossy step (`toGa4Params`) is deliberately here rather than in the shared
 * layer. Under Google Tag Manager one `dataLayer` push feeds six destinations,
 * so trimming to GA4's 25 parameters before that fan-out would hand Meta,
 * TikTok, LinkedIn, Google Ads and Clarity GA4's leftovers even where they had
 * room for more. Each vendor's adapter applies its own limits, as late as
 * possible, and no destination inherits a constraint that is not its own.
 *
 * Acquisition dimensions (source, medium, campaign, landing page, referrer,
 * device, country, new vs returning) are collected by GA4 itself from the
 * `page_view` plus the UTM query string; nothing here re-sends them.
 */

import {
  ANALYTICS_EVENTS,
  PRODUCT_EVENT_ALIASES,
  sanitizeEventProperties,
  toCanonicalEventName,
  type AnalyticsEventName,
  type EventPropertyValue,
} from "@tabbio/utils/analytics/events";

// Re-exported so existing GA4 call sites keep working and so the adapter reads
// as one import. New destinations should import from `./events` directly.
export {
  ANALYTICS_EVENTS,
  CUSTOM_DIMENSIONS,
  FUNNEL_STEPS,
  KEY_EVENTS,
  PRODUCT_EVENT_ALIASES,
  isCriticalAnalyticsEvent,
  isFunnelEvent,
  sanitizeEventProperties,
  toCanonicalEventName,
  type AnalyticsEventName,
  type CustomDimension,
  type EventPropertyValue,
} from "@tabbio/utils/analytics/events";

/** Historical alias. `ANALYTICS_EVENTS` is the vendor-neutral name. */
export const GA4_EVENTS = ANALYTICS_EVENTS;
export type Ga4EventName = AnalyticsEventName;

/** @deprecated use `PRODUCT_EVENT_ALIASES` from `./events`. */
export const PRODUCT_EVENT_TO_GA4_EVENT = PRODUCT_EVENT_ALIASES;

/** @deprecated use `toCanonicalEventName` from `./events`. */
export const toGa4EventName = toCanonicalEventName;

/**
 * The events GA4 receives. Everything else stays in PostHog.
 *
 * The name ceiling is an internal reporting budget, not a GA4 web-stream
 * platform limit. Keep detailed operational names in PostHog and preserve
 * feature/action distinctions when intentionally rolling up GA4 events.
 *
 * This list is the single source for two things that must agree: the app-web
 * forwarder (`shouldForwardProductEventToGa4`) and the Custom Event trigger in
 * the generated Tag Manager container (`gtm-container.ts`). Names here are the
 * canonical ones, after `PRODUCT_EVENT_ALIASES`; several are catalogue entries
 * not yet instrumented, which is deliberate so the container does not need
 * regenerating each time one lands.
 */
export const GA4_EVENT_ALLOW_LIST: readonly string[] = [
  // Landing
  "page_view",
  "landing_view",
  "cta_click",
  "pricing_viewed",
  "tool_used",
  "web_vitals",
  "signup_failed",
  "tool_run_started",
  // Signup
  "signup_started",
  "signup_completed",
  // Onboarding
  "onboarding_started",
  "onboarding_step_completed",
  "onboarding_intent_selected",
  "onboarding_completed",
  // CV
  "cv_created",
  "cv_imported",
  "cv_tailored_created",
  "cv_exported",
  "cv_shared",
  "profile_published",
  // Jobs
  "job_search",
  "job_viewed",
  "job_apply_started",
  "job_apply_completed",
  "automation_activated",
  "auto_apply_run_completed",
  // Product
  "chat_message_sent",
  "feature_used",
  "skill_applied",
  "cv_score_checked",
  // Employer
  "company_created",
  "job_posted",
  // Revenue
  "paywall_viewed",
  "paywall_cta_clicked",
  "checkout_started",
  "trial_started",
  "subscription_started",
  "purchase",
  "subscription_cancelled",
  "subscription_expired",
  "subscription_billing_issue_detected",
  "subscription_plan_changed",
  "refund",
  "subscription_seat_capacity_blocked",
  // Account
  "account_deleted",
  // Public profile demand (server-side, visitor consent explicit) and the web jobs tree
  "public_profile_contact_clicked",
  "cv_downloaded_by_visitor",
  "job_apply_clicked",
  // Server truths from the second sweep
  "ai_action_limit_reached",
  "auto_apply_submitted",
  "public_apply_profile_claimed",
  "partner_referral_attributed",
  // Web funnels from the second sweep (client-side)
  "resume_handoff_started",
  "skill_apply_started",
  "tool_lead_captured",
  "profile_contact_form_submitted",
  "profile_upsell_shown",
  "apply_step_completed",
  "consent_banner_shown",
  "consent_decision_saved",
  "consent_preferences_opened",
  "page_not_found",
  // Leads (the pixel contract maps both) and the employer funnel's server truth
  "handle_claim_started",
  "employer_lead_submitted",
  "application_received",
  "subscription_reactivated",
  "public_profile_viewed",
  "public_profile_cta_clicked",
  "paywall_tier_selected",
  "skill_viewed",
  "chat_opened",
  // The public CV Health funnel: its start and its lead. The list is at its
  // 70-name ceiling with these two; the funnel's other six events
  // (`cv_health_public_*` in ANALYTICS_EVENTS) stay PostHog-only, and the
  // conversion itself is `cta_click` with `cta_id: cv_health_fix`.
  "cv_health_public_review_started",
  "cv_health_public_email_captured",
];

const GA4_EVENT_ALLOW_SET: ReadonlySet<string> = new Set(GA4_EVENT_ALLOW_LIST);

/**
 * Whether an internal product event should reach the data layer at all. Judged
 * on the canonical name, so an aliased internal event
 * (`subscription_purchase_started` → `checkout_started`) passes on the strength
 * of its alias. Server-owned events pass too: the advertising tags need them in
 * the browser, and the GA4 tag's own trigger (`ga4TagTriggerRegex`) is what
 * keeps GA4 from counting them twice.
 */
export function shouldForwardProductEventToGa4(event: string): boolean {
  return resolveGa4Destination(event) !== null;
}

/**
 * Service events GA4 receives as one `feature_used` event with `feature` and
 * `action` as dimensions, while PostHog keeps the specific name. One GA4 event
 * name for the whole services surface keeps the internal name budget for the
 * funnel; `feature` is a registered custom dimension, so every service still
 * reports separately. Every event the studio tools fire
 * (`STUDIO_TOOL_ANALYTICS_EVENTS` in apps/mobile) must appear here.
 */
export const GA4_FEATURE_ROLLUP: Readonly<
  Record<string, { feature: string; action: string }>
> = {
  cover_letter_created: { feature: "cover_letter", action: "created" },
  interview_prep_started: { feature: "interview_prep", action: "started" },
  interview_prep_completed: { feature: "interview_prep", action: "completed" },
  salary_insight_viewed: { feature: "salary_insights", action: "viewed" },
  application_pack_created: { feature: "application_pack", action: "created" },
  recruiter_preview_generated: {
    feature: "recruiter_preview",
    action: "created",
  },
  library_project_created: { feature: "library", action: "created" },
  shortlist_created: { feature: "shortlists", action: "created" },
};

export type Ga4Destination = {
  /** The GA4 event name to send. */
  name: string;
  /** Parameters the destination adds (the rollup's `feature` and `action`). */
  params: Record<string, EventPropertyValue>;
};

/**
 * Where a product event goes in the data layer, if anywhere: its own
 * allow-listed name, the `feature_used` rollup, or nowhere (PostHog-only).
 * Judged on the canonical name, so an aliased internal event passes on the
 * strength of its alias. Tag Manager decides which tags a pushed event reaches.
 */
export function resolveGa4Destination(event: string): Ga4Destination | null {
  const canonical = toCanonicalEventName(event);
  if (GA4_EVENT_ALLOW_SET.has(canonical))
    return { name: canonical, params: {} };
  const rollup = GA4_FEATURE_ROLLUP[canonical];
  if (rollup) {
    return {
      name: "feature_used",
      params: { feature: rollup.feature, action: rollup.action },
    };
  }
  return null;
}

/**
 * Events the API sends to GA4 over the Measurement Protocol. They stay on the
 * allow-list and clients still push them to the data layer, because the Meta,
 * TikTok, LinkedIn and Google Ads tags fire on them there; but the GA4 event
 * tag's trigger excludes them (`ga4TagTriggerRegex`), so GA4 counts each once,
 * from the server.
 *
 * These are the money and identity truths: a signup exists once the database
 * row does, whatever surface or provider created it (email, Google, Apple, UAE
 * PASS, native), and a purchase exists when RevenueCat says so. The server sees
 * all of those; a client sees only its own happy path. The client keeps its
 * copies for PostHog, where a replay of the surrounding session is the point.
 */
export const GA4_SERVER_OWNED_EVENTS: readonly string[] = [
  "signup_completed",
  "trial_started",
  "subscription_started",
  "purchase",
  "subscription_cancelled",
  // The rest of the subscription lifecycle and the account's end, all known
  // only to the server (RevenueCat webhooks, the deletion request).
  "subscription_expired",
  "subscription_billing_issue_detected",
  "subscription_plan_changed",
  "refund",
  "subscription_seat_capacity_blocked",
  "account_deleted",
  "public_profile_contact_clicked",
  "cv_downloaded_by_visitor",
  "ai_action_limit_reached",
  "auto_apply_submitted",
  "public_apply_profile_claimed",
  "partner_referral_attributed",
  "application_received",
  // Companies are created in the API for the app and for partner apps alike,
  // so the server copy is the one GA4 counts; the client keeps its onboarding
  // alias for PostHog.
  "company_created",
  // Lifecycle facts the API records for every creation, import and export
  // path (onboarding, chat, handoff, skills), forwarded over the Measurement
  // Protocol. `cv_shared` stays client-owned: only the client knows the channel.
  "cv_created",
  "cv_imported",
  "cv_exported",
  "subscription_reactivated",
  "public_profile_viewed",
  "public_profile_cta_clicked",
];

const GA4_SERVER_OWNED_EVENT_SET: ReadonlySet<string> = new Set(
  GA4_SERVER_OWNED_EVENTS,
);

export function isGa4ServerOwnedEvent(event: string): boolean {
  return GA4_SERVER_OWNED_EVENT_SET.has(toCanonicalEventName(event));
}

/** The events the GA4 event tag in Tag Manager fires on: the allow-list minus the server-owned names. */
export const GA4_TAG_EVENTS: readonly string[] = GA4_EVENT_ALLOW_LIST.filter(
  (name) => !GA4_SERVER_OWNED_EVENT_SET.has(name),
);

/** The GA4 event tag's Custom Event trigger regex, anchored. */
export function ga4TagTriggerRegex(): string {
  return `^(${[...GA4_TAG_EVENTS].sort().join("|")})$`;
}

// GA4 collection limits. Exceeding them does not error: the offending event or
// parameter is silently dropped or truncated server-side, which is why they are
// enforced here where a test can see it.
// https://support.google.com/analytics/answer/9267744
export const GA4_MAX_EVENT_NAME_LENGTH = 40;
export const GA4_MAX_PARAM_NAME_LENGTH = 40;
export const GA4_MAX_PARAM_VALUE_LENGTH = 100;
export const GA4_MAX_PARAMS_PER_EVENT = 25;

/** GA4 event names must be alphanumeric + underscore, and start with a letter. */
export function isValidGa4EventName(event: string): boolean {
  return (
    event.length > 0 &&
    event.length <= GA4_MAX_EVENT_NAME_LENGTH &&
    /^[A-Za-z][A-Za-z0-9_]*$/.test(event)
  );
}

export type Ga4ParamValue = EventPropertyValue;

/**
 * Applies GA4's own limits to already-sanitized properties: 25 parameters, 40
 * character names, 100 character values, and no `ga_`, `google_` or `firebase_`
 * prefix (GA4 reserves all three).
 *
 * Separate from `sanitizeEventProperties` so that the shared shaping can run
 * once for every destination while only GA4 pays GA4's ceiling.
 */
export function applyGa4Limits(
  properties: Record<string, EventPropertyValue>,
): Record<string, Ga4ParamValue> {
  const result: Record<string, Ga4ParamValue> = {};
  for (const key of Object.keys(properties).sort()) {
    if (Object.keys(result).length >= GA4_MAX_PARAMS_PER_EVENT) break;

    if (/^(ga_|google_|firebase_)/.test(key)) continue;
    const name = key.slice(0, GA4_MAX_PARAM_NAME_LENGTH);

    const value = properties[key]!;
    result[name] =
      typeof value === "string"
        ? value.slice(0, GA4_MAX_PARAM_VALUE_LENGTH)
        : value;
  }
  return result;
}

/**
 * The full GA4 shaping pipeline: neutral scrub, then GA4's ceiling.
 *
 * What the sinks call. Kept as one function so a call site cannot accidentally
 * apply the limits without the scrub, or the scrub twice.
 */
export function toGa4Params(
  properties?: Record<string, unknown> | null,
): Record<string, Ga4ParamValue> {
  return applyGa4Limits(sanitizeEventProperties(properties));
}

/**
 * Consent Mode v2 signals, derived from the two decisions Tabbio's consent UI
 * offers: analytics, and marketing.
 *
 * `analytics_storage` follows the analytics choice. The three advertising
 * signals follow the marketing choice, and default to denied when no marketing
 * decision is supplied: an ad signal must never be granted on the strength of
 * an analytics consent, because that is a consent the visitor was not shown.
 * Under Tag Manager these signals are also what decides whether the Meta,
 * TikTok, LinkedIn and Google Ads tags fire at all.
 */
export function ga4ConsentSignals(
  analyticsGranted: boolean,
  marketingGranted = false,
) {
  const ads = marketingGranted ? "granted" : "denied";
  return {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: analyticsGranted ? "granted" : "denied",
  } as const;
}

/**
 * The Consent Mode `default` payload.
 *
 * Every signal defaults to denied, analytics included. GA4 still receives
 * cookieless pings in that state, which is what keeps acquisition
 * (source/medium/campaign/landing page/referrer) measurable for a visitor who
 * never answers the banner, while nothing identifying is stored on their device
 * until they do.
 */
export function ga4ConsentDefault() {
  return {
    ...ga4ConsentSignals(false),
    // Holds the first hit briefly so a stored "granted" decision can be
    // replayed before it is sent, rather than the session opening as denied and
    // then being modelled.
    wait_for_update: 500,
  };
}

/** The `config` payload both apps use. */
export function ga4ConfigParams() {
  return {
    // Page views are sent from application code instead. Both apps route
    // through the History API, and gtag's own history listener fires before the
    // new route's title is in the document, so leaving it on attaches the
    // previous page's title to the new page's view.
    send_page_view: false,
    // Keeps UTMs on the URL across a redirect when cookies are unavailable.
    url_passthrough: true,
    // No ad signal is granted, so no ad identifier should be sent.
    ads_data_redaction: true,
  };
}

/** The URL of the gtag.js library for a measurement ID. */
export function ga4LibraryUrl(measurementId: string) {
  return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId,
  )}`;
}

/**
 * The inline bootstrap for a GA4 tag, as source text for a `<script>` element.
 *
 * Source text rather than module code because the ordering is the whole point:
 * `consent default` has to be sitting in `dataLayer` before gtag.js
 * initializes, or the library's first ping is sent under the implicit grant
 * that Consent Mode exists to prevent. An inline script in the document is the
 * only place that ordering does not depend on a bundler or a loading strategy.
 *
 * Used by apps/web, where the document is server-rendered. apps/mobile is a
 * client-rendered SPA with no server-rendered head to put this in, so it builds
 * the same queue from `ga4ConsentDefault` and `ga4ConfigParams` at runtime.
 *
 * Both apps use the same measurement ID: GA4 scopes its client id to the
 * registrable domain, so www.tabbio.com and app.tabbio.com share one visitor
 * and one session. Separate IDs would split every signup away from the campaign
 * that caused it and report it as `(direct)`.
 */
export function ga4BootstrapScript(measurementId: string) {
  return [
    "window.dataLayer = window.dataLayer || [];",
    "function gtag(){dataLayer.push(arguments);}",
    `gtag('consent','default',${JSON.stringify(ga4ConsentDefault())});`,
    "gtag('js', new Date());",
    `gtag('config',${JSON.stringify(measurementId)},${JSON.stringify(
      ga4ConfigParams(),
    )});`,
  ].join("\n");
}
